import { Controller } from "@hotwired/stimulus"
import { visit } from "@hotwired/turbo"

const VISIBILITY_LABELS = {
  adult: {
    off: "showing adult/18+",
    include: "only showing adult/18+",
    exclude: "excluding adult/18+",
  },
  unreleased: {
    off: "showing unreleased",
    include: "only showing unreleased",
    exclude: "excluding unreleased",
  },
  canceled: {
    off: "showing canceled",
    include: "only showing canceled",
    exclude: "excluding canceled",
  },
  catalog_hidden: {
    off: "showing curated-hidden",
    include: "only showing curated-hidden",
    exclude: "excluding curated-hidden",
  },
}

const VISIBILITY_DEFAULTS = {
  adult: "exclude",
  unreleased: "off",
  canceled: "exclude",
  catalog_hidden: "off",
}

export default class extends Controller {
  static values = {
    delay: { type: Number, default: 250 },
    baseUrl: { type: String, default: "/games" },
    facetsUrl: { type: String, default: "/games/filter_facets" },
  }
  static targets = [
    "tagLegend",
    "tags",
    "exclude",
    "clearTags",
    "platformLegend",
    "platforms",
    "clearPlatforms",
    "clearQuery",
    "clearAll",
    "query",
    "drawerToggle",
    "drawerPanel",
    "lookingFor",
    "lookingForTags",
    "lookingForInclude",
    "lookingForIncludeChips",
    "lookingForExclude",
    "lookingForExcludeChips",
    "lookingForExcludeLabel",
    "lookingForClearTags",
    "lookingForPlatformInclude",
    "lookingForPlatformIncludeChips",
    "lookingForPlatformIncludeLabel",
    "starters",
    "staleUnreleased",
  ]

  connect() {
    this.timeout = null
    this.facetsAbort = null
    this.boundCloseDrawerOnOutsideClick = this.closeDrawerOnOutsideClick.bind(this)
    this.syncTagParams()
    this.syncPlatformParams()
    this.updateTagLegend()
    this.updatePlatformLegend()
    this.updateClearQuery()
    this.updateClearAll()
    this.syncDrawerToggle()
    this.updateLookingFor()
    document.addEventListener("click", this.boundCloseDrawerOnOutsideClick)
  }

  disconnect() {
    if (this.timeout) clearTimeout(this.timeout)
    this.facetsAbort?.abort()
    document.removeEventListener("click", this.boundCloseDrawerOnOutsideClick)
  }

  navigate(event) {
    event.preventDefault()
    if (this.timeout) clearTimeout(this.timeout)
    this.syncTagParams()
    this.syncPlatformParams()
    this.updateTagLegend()
    this.updatePlatformLegend()
    this.updateLookingFor()
    this.updateClearAll()
    this.refreshFacetCounts()
    this.armScrollToTop()
    visit(this.filterUrl(), { frame: "games_list", action: "advance" })
  }

  submit() {
    this.syncTagParams()
    this.syncPlatformParams()
    this.updateTagLegend()
    this.updatePlatformLegend()
    this.updateLookingFor()
    this.updateClearAll()
    if (this.timeout) clearTimeout(this.timeout)
    this.timeout = setTimeout(() => {
      this.refreshFacetCounts()
      this.armScrollToTop()
      visit(this.filterUrl(), { frame: "games_list", action: "advance" })
    }, this.delayValue)
  }

  // Every path that reloads the results frame through here (tag/platform
  // clicks, clears, visibility toggles, query typing) should land
  // the user back at the top of the results, same as a fresh search --
  // dispatched as a document-level event since the filters form and the
  // games_list frame are siblings, not ancestor/descendant (see
  // ScrollToListController for why a plain data-action can't reach it
  // directly). Pagination links arm themselves directly since they live
  // inside the frame.
  armScrollToTop() {
    document.dispatchEvent(new CustomEvent("games-list:arm-scroll"))
  }

  cycleTag(event) {
    event.preventDefault()
    this.advanceTagState(event.currentTarget)
    this.submit()
  }

  cycleTagKey(event) {
    if (event.key !== " " && event.key !== "Enter") return

    event.preventDefault()
    this.advanceTagState(event.currentTarget)
    this.submit()
  }

  clearTags(event) {
    event.preventDefault()
    this.resetTagChips()
    this.submit()
  }

  clearPlatforms(event) {
    event.preventDefault()
    this.resetPlatformChips()
    this.submit()
  }

  clearLookingFor(event) {
    event.preventDefault()
    this.resetTagChips()
    this.resetPlatformChips()
    this.submit()
  }

  clearAllFilters(event) {
    event.preventDefault()

    this.resetTagChips()
    this.resetPlatformChips()
    this.resetVisibilityChips()
    this.resetAdminFilters()
    this.resetSort()

    const query = this.queryInput()
    if (query) query.value = ""
    this.updateClearQuery()

    this.submit()
  }

  clearQuery(event) {
    event.preventDefault()
    const input = this.queryInput()
    if (!input) return

    input.value = ""
    this.updateClearQuery()
    this.submit()
  }

  onQueryInput() {
    this.updateClearQuery()
    this.submit()
  }

  onVisibilityToggle() {
    this.submit()
  }

  toggleDrawer(event) {
    event.preventDefault()
    event.stopPropagation()
    if (!this.hasDrawerPanelTarget) return

    if (this.drawerPanelTarget.hasAttribute("hidden")) {
      this.openDrawer()
    } else {
      this.closeDrawer()
    }
  }

  openDrawer() {
    if (!this.hasDrawerPanelTarget) return

    this.drawerPanelTarget.hidden = false
    this.element.querySelector(".game-filters")?.classList.add("game-filters--drawer-open")
    this.syncDrawerToggle()
  }

  closeDrawer() {
    if (!this.hasDrawerPanelTarget) return
    if (this.drawerPanelTarget.hasAttribute("hidden")) return

    this.drawerPanelTarget.hidden = true
    this.element.querySelector(".game-filters")?.classList.remove("game-filters--drawer-open")
    this.syncDrawerToggle()
  }

  closeDrawerOnOutsideClick(event) {
    if (!this.hasDrawerPanelTarget) return
    if (this.drawerPanelTarget.hasAttribute("hidden")) return
    if (this.drawerPanelTarget.contains(event.target)) return
    if (this.hasDrawerToggleTarget && this.drawerToggleTarget.contains(event.target)) return

    this.closeDrawer()
  }

  syncDrawerToggle() {
    if (!this.hasDrawerToggleTarget || !this.hasDrawerPanelTarget) return

    const open = !this.drawerPanelTarget.hasAttribute("hidden")
    this.drawerToggleTarget.setAttribute("aria-expanded", open ? "true" : "false")
    this.drawerToggleTarget.classList.toggle("is-open", open)

    const caret = this.drawerToggleTarget.querySelector(".game-filters__drawer-caret")
    if (!caret) return

    caret.classList.toggle("fa-angle-up", open)
    caret.classList.toggle("fa-angle-down", !open)
  }

  queryInput() {
    if (this.hasQueryTarget) return this.queryTarget
    return this.element.querySelector("#q")
  }

  updateClearQuery() {
    if (!this.hasClearQueryTarget) return

    this.clearQueryTarget.hidden = !this.queryInput()?.value?.trim()
  }

  updateClearAll() {
    if (!this.hasClearAllTarget) return

    this.clearAllTarget.hidden = !this.hasClearableFilters()
  }

  hasClearableFilters() {
    if (this.queryInput()?.value?.trim()) return true
    if (this.checkedTagValues("include").length > 0) return true
    if (this.checkedTagValues("exclude").length > 0) return true
    if (this.checkedPlatformValues("include").length > 0) return true
    if (this.checkedAdminFilterValues("catalog_status").length > 0) return true
    if (this.checkedAdminFilterValues("catalog_role").length > 0) return true
    if (this.hasStaleUnreleasedTarget && !this.staleUnreleasedTarget.disabled) return true

    return Object.entries(VISIBILITY_DEFAULTS).some(([key, defaultState]) => {
      return this.visibilityState(key) !== defaultState
    })
  }

  advanceTagState(label) {
    const include = label.querySelector('[data-tag-role="include"]')
    const exclude = label.querySelector('[data-tag-role="exclude"]')
    if (!include) return

    // Platforms (and any chip without an exclude input): include <-> off.
    if (!exclude) {
      if (include.checked) {
        include.checked = false
        label.dataset.tagFilterState = "off"
      } else {
        include.checked = true
        label.dataset.tagFilterState = "include"
      }
      this.syncVisibilityLabel(label)
      return
    }

    if (include.checked) {
      include.checked = false
      exclude.checked = true
      label.dataset.tagFilterState = "exclude"
    } else if (exclude.checked) {
      exclude.checked = false
      label.dataset.tagFilterState = "off"
    } else {
      include.checked = true
      exclude.checked = false
      label.dataset.tagFilterState = "include"
    }

    this.syncVisibilityLabel(label)
  }

  syncVisibilityLabel(label) {
    const key = label.dataset.visibilityFilter
    if (!key) return

    const text = label.querySelector("[data-visibility-label]")
    if (!text) return

    const state = label.dataset.tagFilterState || "off"
    text.textContent = VISIBILITY_LABELS[key]?.[state] || state
  }

  resetTagChips() {
    this.element.querySelectorAll(".game-filters__tags [data-tag-role='include'], .game-filters__tags [data-tag-role='exclude']").forEach((input) => {
      input.checked = false
    })
    this.element.querySelectorAll(".game-filters__tags [data-tag-filter-state]").forEach((label) => {
      label.dataset.tagFilterState = "off"
    })
  }

  resetPlatformChips() {
    this.element.querySelectorAll(".game-filters__platforms [data-tag-role='include']").forEach((input) => {
      input.checked = false
    })
    this.element.querySelectorAll(".game-filters__platforms [data-tag-filter-state]").forEach((label) => {
      label.dataset.tagFilterState = "off"
    })
  }

  resetVisibilityChips() {
    Object.entries(VISIBILITY_DEFAULTS).forEach(([key, state]) => {
      const label = this.element.querySelector(`[data-visibility-filter="${key}"]`)
      if (!label) return

      const include = label.querySelector('[data-tag-role="include"]')
      const exclude = label.querySelector('[data-tag-role="exclude"]')
      if (include) include.checked = state === "include"
      if (exclude) exclude.checked = state === "exclude"
      label.dataset.tagFilterState = state
      this.syncVisibilityLabel(label)
    })
  }

  // "Clear All" means all -- a sort carried in from a /lists/:slug page
  // (e.g. New Releases' date/desc) or a manual sort click shouldn't survive
  // it, same as every other filter it resets. Disabling (not just blanking)
  // matches how #tags/#exclude/#platforms already drop themselves from the
  // submitted params when empty.
  resetSort() {
    const sort = this.element.querySelector("#sort")
    const dir = this.element.querySelector("#dir")
    if (sort) {
      sort.value = ""
      sort.disabled = true
    }
    if (dir) {
      dir.value = ""
      dir.disabled = true
    }
  }

  resetAdminFilters() {
    this.element.querySelectorAll("[data-admin-filter]").forEach((input) => {
      input.checked = false
    })
    if (this.hasStaleUnreleasedTarget) this.staleUnreleasedTarget.disabled = true
  }

  syncTagParams() {
    const included = this.checkedTagValues("include")
    const excluded = this.checkedTagValues("exclude")

    if (this.hasTagsTarget) {
      this.tagsTarget.value = included.join(",")
      this.tagsTarget.disabled = included.length === 0
    }

    if (this.hasExcludeTarget) {
      this.excludeTarget.value = excluded.join(",")
      this.excludeTarget.disabled = excluded.length === 0
    }
  }

  syncPlatformParams() {
    const included = this.checkedPlatformValues("include")

    if (this.hasPlatformsTarget) {
      this.platformsTarget.value = included.join(",")
      this.platformsTarget.disabled = included.length === 0
    }
  }

  async refreshFacetCounts() {
    const url = this.facetsRequestUrl()
    if (!url) return

    this.facetsAbort?.abort()
    this.facetsAbort = new AbortController()

    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: this.facetsAbort.signal,
      })
      if (!response.ok) return

      const data = await response.json()
      this.applyFacetCounts(data)
    } catch (error) {
      if (error?.name === "AbortError") return
    }
  }

  facetsRequestUrl() {
    const base = this.facetsUrlValue || "/games/filter_facets"
    const filter = this.filterUrl()
    const queryIndex = filter.indexOf("?")
    if (queryIndex === -1) return base

    return `${base}${filter.slice(queryIndex)}`
  }

  applyFacetCounts(data) {
    const tags = data?.tags || {}
    const platforms = data?.platforms || {}
    const visibility = data?.visibility || {}

    this.element.querySelectorAll('[data-filter-count="tag"]').forEach((el) => {
      const key = el.dataset.filterCountKey
      this.setChipCount(el, tags[key] || 0)
    })
    this.element.querySelectorAll('[data-filter-count="platform"]').forEach((el) => {
      const key = el.dataset.filterCountKey
      this.setChipCount(el, platforms[key] || 0)
    })
    this.element.querySelectorAll('[data-filter-count="visibility"]').forEach((el) => {
      const key = el.dataset.filterCountKey
      this.setChipCount(el, visibility[key] || 0)
    })
  }

  setChipCount(countEl, count) {
    countEl.textContent = String(count)
    const tag = countEl.closest(".tag")
    if (!tag) return

    tag.classList.toggle("tag--zero-count", count === 0)

    // Keep each chip's reserved digit width at its high-water mark so
    // 123 → 98 still reserves 3 digits and chips don't reflow.
    const next = Math.max(String(count).length, 1)
    const current = parseInt(tag.style.getPropertyValue("--tag-count-digits"), 10) || 0
    if (next > current) {
      tag.style.setProperty("--tag-count-digits", String(next))
    }
  }

  filterUrl() {
    const params = []
    const tags = this.checkedTagValues("include")
    const exclude = this.checkedTagValues("exclude")
    const platforms = this.checkedPlatformValues("include")
    const query = this.queryInput()?.value?.trim()
    const perPage = this.currentPerPage()
    const adult = this.visibilityState("adult")
    const unreleased = this.visibilityState("unreleased")
    const canceled = this.visibilityState("canceled")
    const catalogHidden = this.visibilityState("catalog_hidden")
    const statuses = this.checkedAdminFilterValues("catalog_status")
    const roles = this.checkedAdminFilterValues("catalog_role")
    const sort = this.currentSort()
    const dir = this.currentDir()

    if (tags.length > 0) params.push(`tags=${this.encodeList(tags)}`)
    if (exclude.length > 0) params.push(`exclude=${this.encodeList(exclude)}`)
    if (platforms.length > 0) params.push(`platforms=${this.encodeList(platforms)}`)
    if (query) params.push(`q=${encodeURIComponent(query)}`)
    if (perPage) params.push(`per_page=${encodeURIComponent(perPage)}`)
    if (adult !== VISIBILITY_DEFAULTS.adult) params.push(`adult=${encodeURIComponent(adult)}`)
    if (unreleased !== VISIBILITY_DEFAULTS.unreleased) {
      params.push(`unreleased=${encodeURIComponent(unreleased)}`)
    }
    if (canceled !== VISIBILITY_DEFAULTS.canceled) {
      params.push(`canceled=${encodeURIComponent(canceled)}`)
    }
    if (catalogHidden !== VISIBILITY_DEFAULTS.catalog_hidden) {
      params.push(`catalog_hidden=${encodeURIComponent(catalogHidden)}`)
    }
    if (statuses.length > 0) params.push(`catalog_status=${this.encodeList(statuses)}`)
    if (roles.length > 0) params.push(`catalog_role=${this.encodeList(roles)}`)
    if (this.hasStaleUnreleasedTarget && !this.staleUnreleasedTarget.disabled) {
      params.push("stale_unreleased=1")
    }
    if (sort && sort !== "rating") params.push(`sort=${encodeURIComponent(sort)}`)
    if (dir && !this.isDefaultSortDir(sort || "rating", dir)) {
      params.push(`dir=${encodeURIComponent(dir)}`)
    }

    const base = this.baseUrlValue || "/games"
    return params.length > 0 ? `${base}?${params.join("&")}` : base
  }

  visibilityState(key) {
    const label = this.element.querySelector(`[data-visibility-filter="${key}"]`)
    return label?.dataset.tagFilterState || VISIBILITY_DEFAULTS[key]
  }

  checkedAdminFilterValues(name) {
    return Array.from(this.element.querySelectorAll(`[data-admin-filter="${name}"]:checked`)).map(
      (input) => input.value,
    )
  }

  // Prefer the address bar so page-size links inside the turbo frame stay in sync.
  currentPerPage() {
    const fromUrl = new URL(window.location.href).searchParams.get("per_page")
    if (fromUrl) return fromUrl

    return this.element.querySelector("#per_page")?.value
  }

  currentSort() {
    const fromUrl = new URL(window.location.href).searchParams.get("sort")
    if (fromUrl) return fromUrl

    return this.element.querySelector("#sort")?.value || "rating"
  }

  currentDir() {
    const fromUrl = new URL(window.location.href).searchParams.get("dir")
    if (fromUrl) return fromUrl

    const el = this.element.querySelector("#dir")
    if (el && !el.disabled) return el.value

    return this.defaultSortDir(this.currentSort())
  }

  defaultSortDir(field) {
    if (field === "rating" || field === "date") return "desc"
    return "asc"
  }

  isDefaultSortDir(field, dir) {
    return dir === this.defaultSortDir(field)
  }

  // Keep commas readable in the address bar; still escape each tag value.
  encodeList(values) {
    return values.map((value) => encodeURIComponent(value)).join(",")
  }

  checkedTagValues(role) {
    return Array.from(
      this.element.querySelectorAll(`.game-filters__tags [data-tag-role="${role}"]:checked`),
    ).map((input) => input.value)
  }

  checkedPlatformValues(role) {
    return Array.from(
      this.element.querySelectorAll(`.game-filters__platforms [data-tag-role="${role}"]:checked`),
    ).map((input) => input.value)
  }

  updateTagLegend() {
    if (!this.hasTagLegendTarget) return

    const selectedCount = this.checkedTagValues("include").length
    const excludedCount = this.checkedTagValues("exclude").length
    const filtering = selectedCount > 0 || excludedCount > 0

    this.tagLegendTarget.textContent = this.chipFilterLegendText("Tags", selectedCount, excludedCount)

    if (this.hasClearTagsTarget) {
      this.clearTagsTarget.hidden = !filtering
    }
  }

  updateLookingFor() {
    if (!this.hasLookingForTarget) return

    const included = []
    const excluded = []
    this.element.querySelectorAll(".game-filters__tags [data-tag-filter-state]").forEach((label) => {
      const state = label.dataset.tagFilterState
      if (state !== "include" && state !== "exclude") return

      const name = label.querySelector("[data-chip-name]")?.textContent?.trim()
      if (!name) return

      const chip = {
        name,
        state,
        iconClass: label.dataset.tagIconClass || "",
        toneClass: label.dataset.tagTone || "",
        mvSignal: label.dataset.tagMvSignal || "",
      }
      if (state === "include") included.push(chip)
      else excluded.push(chip)
    })

    const platformIncluded = []
    this.element.querySelectorAll(".game-filters__platforms [data-tag-filter-state]").forEach((label) => {
      const state = label.dataset.tagFilterState
      if (state !== "include") return

      const nameEl = label.querySelector("[data-chip-name]")
      if (!nameEl) return

      platformIncluded.push({ state, contentHtml: nameEl.innerHTML })
    })

    const hasTags = included.length > 0 || excluded.length > 0
    const hasPlatforms = platformIncluded.length > 0

    if (this.hasLookingForIncludeChipsTarget) {
      this.lookingForIncludeChipsTarget.replaceChildren(
        ...included.map((chip) => this.buildLookingForTagChip(chip)),
      )
    }
    if (this.hasLookingForExcludeChipsTarget) {
      this.lookingForExcludeChipsTarget.replaceChildren(
        ...excluded.map((chip) => this.buildLookingForTagChip(chip)),
      )
    }
    if (this.hasLookingForIncludeTarget) {
      this.lookingForIncludeTarget.hidden = included.length === 0
    }
    if (this.hasLookingForExcludeTarget) {
      this.lookingForExcludeTarget.hidden = excluded.length === 0
    }
    if (this.hasLookingForExcludeLabelTarget) {
      this.lookingForExcludeLabelTarget.textContent =
        included.length === 0 ? "I don't want…" : "But not…"
    }
    if (this.hasLookingForTagsTarget) {
      this.lookingForTagsTarget.hidden = !hasTags
    }

    if (this.hasLookingForPlatformIncludeChipsTarget) {
      this.lookingForPlatformIncludeChipsTarget.replaceChildren(
        ...platformIncluded.map((chip) => this.buildLookingForPlatformChip(chip)),
      )
    }
    if (this.hasLookingForPlatformIncludeTarget) {
      this.lookingForPlatformIncludeTarget.hidden = platformIncluded.length === 0
    }
    if (this.hasLookingForPlatformIncludeLabelTarget) {
      this.lookingForPlatformIncludeLabelTarget.textContent = hasTags
        ? "and I want to play on…"
        : "I want to play on…"
    }

    if (this.hasLookingForClearTagsTarget) {
      this.lookingForClearTagsTarget.hidden = !hasTags && !hasPlatforms
    }

    this.lookingForTarget.hidden = !hasTags && !hasPlatforms

    if (this.hasStartersTarget) {
      this.startersTarget.hidden = hasTags || hasPlatforms
    }
  }

  buildLookingForTagChip({ name, state, iconClass, toneClass, mvSignal }) {
    const chip = document.createElement("span")
    chip.className = ["tag", toneClass, "site-top-bar__looking-for-chip"].filter(Boolean).join(" ")
    chip.dataset.tagFilterState = state

    if (iconClass) {
      const icon = document.createElement("i")
      icon.className = `fa-solid ${iconClass} tag-group-icon`
      icon.setAttribute("aria-hidden", "true")
      chip.appendChild(icon)
    }

    const nameEl = document.createElement("span")
    nameEl.dataset.chipName = ""
    nameEl.className = "tag__label"
    nameEl.textContent = name
    chip.appendChild(nameEl)

    if (mvSignal === "warm" || mvSignal === "cool") {
      const signal = document.createElement("i")
      signal.className =
        mvSignal === "warm"
          ? "fa-solid fa-pepper-hot tag__mv-icon tag__mv-icon--warm"
          : "fa-solid fa-ice-cream tag__mv-icon tag__mv-icon--cool"
      signal.setAttribute("aria-hidden", "true")
      chip.appendChild(signal)
    }

    return chip
  }

  buildLookingForPlatformChip({ state, contentHtml }) {
    const chip = document.createElement("span")
    chip.className = "tag site-top-bar__looking-for-chip"
    chip.dataset.tagFilterState = state

    const nameEl = document.createElement("span")
    nameEl.dataset.chipName = ""
    nameEl.innerHTML = contentHtml
    chip.appendChild(nameEl)

    return chip
  }

  updatePlatformLegend() {
    if (!this.hasPlatformLegendTarget) return

    const selectedCount = this.checkedPlatformValues("include").length
    const filtering = selectedCount > 0

    this.platformLegendTarget.textContent = this.chipFilterLegendText("Platforms", selectedCount, 0)

    if (this.hasClearPlatformsTarget) {
      this.clearPlatformsTarget.hidden = !filtering
    }
  }

  chipFilterLegendText(label, selectedCount, excludedCount) {
    if (selectedCount === 0 && excludedCount === 0) {
      return `${label}: 0 selected`
    }

    const parts = []
    if (selectedCount > 0) parts.push(`${selectedCount} selected`)
    if (excludedCount > 0) parts.push(`${excludedCount} excluded`)
    return `${label}: ${parts.join(", ")}`
  }
}
